import { useEffect, useRef } from 'react'
import { Link } from 'react-router-dom'
import gsap from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { DrumstickIcon, FireIcon, LeafIcon, StarIcon } from '../components/Icons'
import './Philosophy.css'

const philosophyPoints = [
  {
    title: '100% REAL CHICKEN. NO NONSENSE',
    description: 'No lab-grown chicken. No shortcuts. We believe in serving food that\'s as real as our commitment to you.',
    icon: <DrumstickIcon size={55} />
  },
  {
    title: 'BOLD FLAVOURS, BOLDER PROMISES',
    description: 'We don\'t follow food trends. We create flavours that make you come back for more, every single time.',
    icon: <FireIcon size={50} />
  },
  {
    title: 'ETHICAL SOURCING',
    description: 'From farm to your plate - every step is monitored to ensure quality, freshness, and ethical practices.',
    icon: <LeafIcon size={50} />
  },
  {
    title: 'HANDCRAFTED PERFECTION',
    description: 'Every piece of chicken is prepared with care, marinated to perfection, and cooked to crispy excellence.',
    icon: <StarIcon size={45} color="#E4002B" />
  }
]

const process = [
  { step: '01', title: 'Farm Fresh', description: 'Sourced from trusted farms with quality-controlled practices' },
  { step: '02', title: 'Fresh Delivery', description: 'Delivered fresh daily to maintain optimal quality' },
  { step: '03', title: 'Handcrafted', description: 'Marinated and prepared by skilled culinary experts' },
  { step: '04', title: 'Served Hot', description: 'Cooked to order and served at peak freshness' }
]

export default function Philosophy() {
  const pageRef = useRef(null)

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Hero animation
      gsap.fromTo('.philosophy-hero-content > *',
        { opacity: 0, y: 50 },
        { opacity: 1, y: 0, duration: 0.8, stagger: 0.15, ease: 'power3.out', delay: 0.3 }
      )

      // Philosophy cards animation
      gsap.fromTo('.philosophy-card',
        { opacity: 0, y: 60 },
        {
          opacity: 1,
          y: 0,
          duration: 0.8,
          stagger: 0.15,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: '.philosophy-grid',
            start: 'top 75%',
          }
        }
      )

      // Process steps animation
      gsap.fromTo('.process-step',
        { opacity: 0, x: -40 },
        {
          opacity: 1,
          x: 0,
          duration: 0.6,
          stagger: 0.2,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: '.process-section',
            start: 'top 70%',
          }
        }
      )

      // Quote animation
      gsap.fromTo('.quote-content',
        { opacity: 0, scale: 0.95 },
        {
          opacity: 1,
          scale: 1,
          duration: 1,
          ease: 'power3.out',
          scrollTrigger: {
            trigger: '.quote-section',
            start: 'top 70%',
          }
        }
      )
    }, pageRef)

    return () => ctx.revert()
  }, [])

  return (
    <div className="philosophy-page" ref={pageRef}>
      {/* Hero Section */}
      <section className="philosophy-hero">
        <div className="container">
          <div className="philosophy-hero-content">
            <span className="hero-label">Our Philosophy</span>
            <h1 className="hero-title">
              At BIGGUYS, It's Not Just Food
              <span className="highlight"> — It's a Belief</span>
            </h1>
            <p className="hero-description">
              We're driven by a simple belief: great food should be honest food. 
              No compromises. No shortcuts. Just pure, delicious reality.
            </p>
          </div>
        </div>
      </section>

      {/* Philosophy Points */}
      <section className="philosophy-points">
        <div className="container">
          <div className="philosophy-grid">
            {philosophyPoints.map((point, index) => (
              <div className="philosophy-card" key={index}>
                <span className="card-icon">{point.icon}</span>
                <h3 className="card-title">{point.title}</h3>
                <p className="card-description">{point.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="process-section">
        <div className="container">
          <div className="section-header">
            <span className="section-label">Our Process</span>
            <h2 className="section-title">From Farm to Your Plate</h2>
            <p className="section-subtitle">
              Every step in our process is designed to deliver the freshest, 
              most delicious chicken experience possible.
            </p>
          </div>

          <div className="process-grid">
            {process.map((item, index) => (
              <div className="process-step" key={index}>
                <div className="step-number">{item.step}</div>
                <div className="step-content">
                  <h3 className="step-title">{item.title}</h3>
                  <p className="step-description">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quote Section */}
      <section className="quote-section">
        <div className="container">
          <div className="quote-content">
            <blockquote className="quote">
              "We don't follow food trends. We make food you'll crave forever."
            </blockquote>
            <cite className="quote-author">— The BIGGUYS Philosophy</cite>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="philosophy-cta">
        <div className="container">
          <div className="cta-content">
            <h2>Taste What We Believe In</h2>
            <p>Experience the BIGGUYS difference for yourself.</p>
            {/* <Link to="/menu" className="btn btn-primary">Explore Our Menu</Link> */}
          </div>
        </div>
      </section>
    </div>
  )
}
